import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsAPI } from '../../utils/api';

const pick = (settings, key, fallback = '') =>
  (settings[key] ?? fallback) || fallback;

const DEFAULTS = {
  privacy: {
    en: {
      title: 'Privacy Policy',
      body: `Your privacy matters to us. This page explains how we collect, use, and protect your personal information when you shop with Qaya KW.

Information We Collect
We collect the information you provide when placing an order — name, phone, address, and email — along with order details necessary to process and deliver your purchase.

How We Use Your Information
Your information is used solely to process orders, arrange delivery, send order updates, and respond to your inquiries. We do not sell or share your data with third parties for marketing purposes.

Cookies
Our website uses cookies to remember your cart and improve your browsing experience.

Contact
If you have any questions about our privacy practices, please reach out via the Contact page.`,
    },
    ar: {
      title: 'سياسة الخصوصية',
      body: `خصوصيتك تهمنا. توضح هذه الصفحة كيفية جمعنا واستخدامنا وحمايتنا لمعلوماتك الشخصية عند التسوق مع قايا الكويت.

المعلومات التي نجمعها
نجمع المعلومات التي تقدمها عند تقديم الطلب — الاسم، الهاتف، العنوان، والبريد الإلكتروني — إلى جانب تفاصيل الطلب اللازمة لمعالجة وتوصيل مشترياتك.

كيف نستخدم معلوماتك
تُستخدم معلوماتك فقط لمعالجة الطلبات، تنظيم التوصيل، إرسال تحديثات الطلب، والرد على استفساراتك. نحن لا نبيع أو نشارك بياناتك مع أطراف ثالثة لأغراض تسويقية.

ملفات تعريف الارتباط
يستخدم موقعنا ملفات تعريف الارتباط لتذكر سلة التسوق وتحسين تجربة التصفح.

التواصل
إذا كان لديك أي استفسار بشأن ممارساتنا للخصوصية، يرجى التواصل عبر صفحة اتصل بنا.`,
    },
  },
  faq: {
    en: {
      title: 'Frequently Asked Questions',
      body: `How long does delivery take?
Orders placed before 4 PM are typically delivered within 1–2 business days across Kuwait.

Do you ship outside Kuwait?
At the moment we deliver only within Kuwait.

What payment methods do you accept?
We accept KNET, Visa, Mastercard, and Apple Pay through our secure payment gateway.

Is your honey 100% natural?
Yes. We work directly with trusted beekeepers and our honey is raw and unprocessed unless explicitly noted.

How should I store my honey?
Store at room temperature, away from direct sunlight. Crystallization is natural and a sign of pure honey.

Can I return a product?
Due to the nature of food products, we don't accept returns once the seal is broken. If your order arrives damaged, contact us within 48 hours.`,
    },
    ar: {
      title: 'الأسئلة الشائعة',
      body: `كم تستغرق عملية التوصيل؟
الطلبات المقدمة قبل الساعة 4 مساءً تُسلَّم عادةً خلال يوم إلى يومي عمل داخل الكويت.

هل تشحنون خارج الكويت؟
حالياً نوصل داخل الكويت فقط.

ما هي طرق الدفع المقبولة؟
نقبل KNET، فيزا، ماستركارد، وApple Pay عبر بوابة الدفع الآمنة.

هل العسل طبيعي 100٪؟
نعم. نعمل مباشرةً مع نحالين موثوقين وعسلنا خام وغير معالج ما لم يُذكر خلاف ذلك.

كيف أحفظ العسل؟
يُحفظ في درجة حرارة الغرفة، بعيداً عن أشعة الشمس المباشرة. التبلور أمر طبيعي ودليل على نقاء العسل.

هل يمكنني إرجاع منتج؟
نظراً لطبيعة المنتجات الغذائية، لا نقبل الإرجاع بعد فتح الختم. إذا وصل طلبك متضرراً، يرجى التواصل معنا خلال 48 ساعة.`,
    },
  },
  shipping: {
    en: {
      title: 'Shipping & Delivery',
      body: `Delivery Areas
We deliver throughout the State of Kuwait.

Delivery Time
Orders placed before 4 PM are usually delivered within 1–2 business days. Same-day delivery may be available for orders placed in the morning, depending on availability.

Delivery Fees
Delivery fees are calculated at checkout based on your order total. Free delivery is available on qualifying orders.

Order Tracking
You'll receive order updates via SMS and email. You can also check the status of your order anytime through your account page.

Damaged or Missing Items
If your order arrives damaged or with missing items, please contact us within 48 hours of delivery so we can make it right.`,
    },
    ar: {
      title: 'الشحن والتوصيل',
      body: `مناطق التوصيل
نوصل في جميع أنحاء دولة الكويت.

وقت التوصيل
الطلبات المقدمة قبل الساعة 4 مساءً تُسلَّم عادةً خلال يوم إلى يومي عمل. قد يتوفر التوصيل في نفس اليوم للطلبات المقدمة صباحاً، وذلك حسب التوفر.

رسوم التوصيل
تُحتسب رسوم التوصيل عند إتمام الطلب بناءً على إجمالي طلبك. التوصيل مجاني للطلبات التي تستوفي الحد الأدنى.

تتبع الطلب
ستصلك تحديثات الطلب عبر الرسائل النصية والبريد الإلكتروني. يمكنك أيضاً متابعة حالة طلبك في أي وقت من خلال صفحة حسابك.

المنتجات التالفة أو المفقودة
إذا وصل طلبك متضرراً أو ناقصاً، يرجى التواصل معنا خلال 48 ساعة من التسليم لنتمكن من معالجة الأمر.`,
    },
  },
};

const LegalPage = ({ keyPrefix }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const suf = isAr ? '_ar' : '';

  useEffect(() => {
    settingsAPI.getPublic()
      .then(rows => setSettings(Object.fromEntries((rows || []).map(r => [r.setting_key, r.setting_value]))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="spinner" />
    </div>
  );

  const v = (key, fallback) => pick(settings, key + suf, pick(settings, key, fallback));

  const defs = DEFAULTS[keyPrefix] || { en: { title: '', body: '' }, ar: { title: '', body: '' } };
  const def = isAr ? defs.ar : defs.en;

  const title = v(`${keyPrefix}_title`, def.title);
  const body = v(`${keyPrefix}_body`, def.body);

  // Parse body: split into blocks separated by blank lines.
  // First line of each block (when followed by content) is treated as a heading.
  const blocks = (body || '').split(/\n{2,}/).map(b => b.trim()).filter(Boolean);

  return (
    <div className="legal-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="container">
        <header className="legal-page__header">
          <h1>{title}</h1>
        </header>

        <article className="legal-page__content">
          {blocks.map((block, i) => {
            const lines = block.split(/\n+/);
            // If the block has multiple lines, treat the first line as a sub-heading
            if (lines.length > 1) {
              const [heading, ...rest] = lines;
              return (
                <section key={i} className="legal-block">
                  <h2>{heading}</h2>
                  {rest.map((line, j) => <p key={j}>{line}</p>)}
                </section>
              );
            }
            // Single-line block — just a paragraph
            return <p key={i} className="legal-para">{block}</p>;
          })}
        </article>
      </div>

      <style>{`
        .legal-page { padding: 30px 0 80px; }
        .legal-page__header { text-align: center; margin-bottom: 40px; }
        .legal-page__header h1 {
          font-family: var(--font-serif); font-size: 2.4rem;
          color: var(--olive, #4a5e2a);
        }
        .legal-page__content {
          max-width: 760px; margin: 0 auto;
          background: var(--white); border-radius: var(--radius-lg);
          padding: 40px;
        }
        .legal-block { margin-bottom: 28px; }
        .legal-block:last-child { margin-bottom: 0; }
        .legal-block h2 {
          font-family: var(--font-serif); font-size: 1.25rem;
          color: var(--olive, #4a5e2a); margin-bottom: 10px;
        }
        .legal-block p, .legal-para {
          color: var(--text-muted); line-height: 1.85;
          margin-bottom: 10px; font-size: 0.98rem;
        }
        .legal-block p:last-child, .legal-para:last-child { margin-bottom: 0; }
        @media (max-width: 600px) {
          .legal-page__header h1 { font-size: 1.8rem; }
          .legal-page__content { padding: 24px; }
        }
      `}</style>
    </div>
  );
};

export default LegalPage;
